import Swal from "sweetalert2";

/**
 * Unified SweetAlert2 Service
 * Provides consistent styling and API across the entire application
 * Works in both React components and Blade templates
 */

// Default configuration for all dialogs
const defaultConfig = {
  width: "400px",
  padding: "1.5rem",
  allowOutsideClick: true,
  allowEscapeKey: true,
  showCloseButton: false,
  buttonsStyling: false,
  customClass: {
    popup: "swal2-popup-unified",
    title: "swal2-title-unified",
    content: "swal2-content-unified",
    confirmButton: "btn btn-sm",
    cancelButton: "btn btn-sm",
  },
};

// Force cleanup function for emergency situations
const forceCleanup = () => {
  // Remove SweetAlert2 classes from body and html
  document.body.classList.remove(
    'swal2-shown',
    'swal2-height-auto',
    'swal2-no-backdrop',
    'swal2-toast-shown'
  );
  document.documentElement.classList.remove(
    'swal2-shown', 
    'swal2-height-auto'
  );
  
  // Reset body styles
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
  
  // Remove any leftover containers
  document.querySelectorAll('.swal2-container').forEach(el => el.remove());
};

// Cleanup function to remove unwanted elements without breaking functionality
const cleanupPopup = (popup, isCustomDialog = false) => {
  if (!popup) return;

  // Only remove validation messages - never touch core elements
  const unwantedElements = popup.querySelectorAll(".swal2-validation-message");
  unwantedElements.forEach((el) => el.remove());

  // For standard dialogs without inputs, adjust spacing
  if (!isCustomDialog) {
    const content = popup.querySelector(".swal2-html-container");
    const hasInputs = popup.querySelector("input, textarea, select");
    if (content && !hasInputs) {
      content.style.marginBottom = "0";
      content.style.paddingBottom = "0";
    }
  }
};

// Base fire method with unified defaults
const fire = (config) => {
  const mergedConfig = {
    ...defaultConfig,
    ...config,
    customClass: {
      ...defaultConfig.customClass,
      ...(config.customClass || {}),
    },
  };

  // Check if this is a custom dialog (has html property)
  const isCustomDialog = !!config.html;

  // Return the Promise directly without .catch()
  return Swal.fire({
    ...mergedConfig,
    didOpen: () => {
      // Use setTimeout to ensure SweetAlert2's internal setup completes first
      setTimeout(() => {
        cleanupPopup(Swal.getPopup(), isCustomDialog);
        
        // Call custom didOpen if provided
        if (config.didOpen) {
          config.didOpen();
        }
      }, 0);
    },
    willClose: () => {
      // Ensure cleanup happens before closing
      forceCleanup();
      
      // Call custom willClose if provided
      if (config.willClose) {
        config.willClose();
      }
    },
    didClose: () => {
      // Final cleanup after modal is closed
      forceCleanup();
      
      // Call custom didClose if provided
      if (config.didClose) {
        config.didClose();
      }
    },
  }).then((result) => {
    // Handle successful completion
    return result;
  }, (error) => {
    // Handle any errors in the promise
    forceCleanup();
    // Re-throw the error so the caller can handle it
    throw error;
  });
};

const swalService = {
  /**
   * Confirmation dialog
   * @param {string} message - Confirmation message
   * @param {string} title - Dialog title (default: 'Confirm')
   * @param {string} confirmText - Confirm button text (default: 'Confirm')
   * @param {string} cancelText - Cancel button text (default: 'Cancel')
   * @param {string} icon - Icon type (default: 'question')
   * @param {string} confirmButtonClass - Confirm button class (default: 'btn-success')
   * @returns {Promise} SweetAlert2 result
   */
  confirm: (
    message,
    title = "Confirm",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = "question",
    confirmButtonClass = "btn-success"
  ) => {
    return fire({
      title,
      text: message,
      icon,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      customClass: {
        confirmButton: `btn btn-sm ${confirmButtonClass}`,
        cancelButton: "btn btn-sm btn-secondary",
      },
    });
  },

  /**
   * Alert dialog (success, error, warning, info)
   * @param {string} message - Alert message
   * @param {string} type - Alert type: 'success', 'error', 'warning', 'info' (default: 'info')
   * @param {string} title - Dialog title
   * @param {string} confirmText - Confirm button text (default: 'OK')
   * @returns {Promise} SweetAlert2 result
   */
  alert: (message, type = "info", title = null, confirmText = "OK") => {
    const titles = {
      success: "Success",
      error: "Error",
      warning: "Warning",
      info: "Information",
    };

    const buttonClasses = {
      success: "btn-success",
      error: "btn-danger",
      warning: "btn-warning",
      info: "btn-info",
    };

    return fire({
      title: title || titles[type] || "Information",
      text: message,
      icon: type,
      showCancelButton: false,
      confirmButtonText: confirmText,
      customClass: {
        confirmButton: `btn btn-sm ${buttonClasses[type] || "btn-primary"}`,
      },
    });
  },

  /**
   * Success alert
   */
  success: (message, title = "Success", confirmText = "OK") => {
    return swalService.alert(message, "success", title, confirmText);
  },

  /**
   * Error alert - FIXED: Now uses unified approach
   */
  error: (message, title = "Error", confirmText = "OK") => {
    // Close any existing dialogs first
    if (Swal.isVisible()) {
      Swal.close();
      forceCleanup();
    }

    // Use the unified fire method instead of direct Swal.fire
    return fire({
      title: title || "Error",
      text: message,
      icon: "error",
      confirmButtonText: confirmText,
      showCancelButton: false,
      customClass: {
        confirmButton: "btn btn-sm btn-danger",
      },
    });
  },

  /**
   * Warning alert
   */
  warning: (message, title = "Warning", confirmText = "OK") => {
    return swalService.alert(message, "warning", title, confirmText);
  },

  /**
   * Info alert
   */
  info: (message, title = "Information", confirmText = "OK") => {
    return swalService.alert(message, "info", title, confirmText);
  },

  /**
   * Text input dialog
   * @param {string} message - Input prompt message
   * @param {string} title - Dialog title
   * @param {string} inputType - Input type: 'text', 'number', 'email', etc. (default: 'text')
   * @param {string} placeholder - Input placeholder
   * @param {string} inputValue - Default input value
   * @param {Function} inputValidator - Validation function
   * @returns {Promise} SweetAlert2 result
   */
  input: (
    message,
    title = "Input",
    inputType = "text",
    placeholder = "",
    inputValue = "",
    inputValidator = null
  ) => {
    return fire({
      title,
      text: message,
      input: inputType,
      inputPlaceholder: placeholder,
      inputValue,
      inputValidator,
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-sm btn-primary",
        cancelButton: "btn btn-sm btn-secondary",
        input: "swal2-input-unified",
      },
      didOpen: () => {
        // Focus input after modal opens
        setTimeout(() => {
          const popup = Swal.getPopup();
          if (popup) {
            const input = popup.querySelector(".swal2-input");
            if (input) {
              input.focus();
              input.select();
            }
          }
        }, 100);
      },
    });
  },

  /**
   * Radio button selection dialog
   * @param {string} message - Selection prompt message
   * @param {string} title - Dialog title
   * @param {Object} options - Radio options {value: 'label'}
   * @param {Function} inputValidator - Validation function
   * @returns {Promise} SweetAlert2 result
   */
  radio: (message, title, options, inputValidator = null) => {
    return fire({
      title,
      text: message,
      input: "radio",
      inputOptions: options,
      inputValidator:
        inputValidator ||
        ((value) => {
          if (!value) {
            return "Please select an option";
          }
        }),
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-sm btn-primary",
        cancelButton: "btn btn-sm btn-secondary",
        popup: "swal2-popup-unified swal2-popup-radio",
      },
    });
  },

  /**
   * Custom HTML dialog
   * @param {string} html - HTML content
   * @param {string} title - Dialog title
   * @param {Object} options - Additional SweetAlert2 options
   * @returns {Promise} SweetAlert2 result
   */
  custom: (html, title = "", options = {}) => {
    return fire({
      title,
      html,
      width: options.width || "600px",
      showCancelButton: options.showCancelButton !== false,
      confirmButtonText: options.confirmButtonText || "Submit",
      cancelButtonText: options.cancelButtonText || "Cancel",
      preConfirm: options.preConfirm,
      customClass: {
        confirmButton: `btn btn-sm ${
          options.confirmButtonClass || "btn-primary"
        }`,
        cancelButton: "btn btn-sm btn-secondary",
        popup: "swal2-popup-unified swal2-popup-custom",
      },
      ...options,
    });
  },

  /**
   * Delete confirmation dialog
   * @param {string} itemName - Name of item to delete
   * @param {string} message - Custom message (optional)
   * @returns {Promise} SweetAlert2 result
   */
  deleteConfirm: (itemName = "this item", message = null) => {
    return fire({
      title: "Are you sure?",
      text:
        message ||
        `Do you really want to delete ${itemName}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-sm btn-danger",
        cancelButton: "btn btn-sm btn-secondary",
      },
    });
  },

  /**
   * Toast notification
   * @param {string} message - Toast message
   * @param {string} type - Toast type: 'success', 'error', 'warning', 'info' (default: 'success')
   * @param {string} position - Toast position (default: 'top-end')
   * @param {number} timer - Auto-close timer in ms (default: 3000)
   * @returns {Promise} SweetAlert2 result
   */
  toast: (message, type = "success", position = "top-end", timer = 3000) => {
    const Toast = Swal.mixin({
      toast: true,
      position,
      showConfirmButton: false,
      timer,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
      didClose: () => {
        forceCleanup();
      },
    });

    return Toast.fire({
      icon: type,
      title: message,
    });
  },

  /**
   * Emergency force close - useful for debugging
   */
  forceClose: () => {
    if (Swal.isVisible()) {
      Swal.close();
    }
    forceCleanup();
  },

  /**
   * Check if Swal is visible
   */
  isVisible: () => {
    return Swal.isVisible();
  },

  /**
   * Low-level fire method for advanced use cases
   * @param {Object} config - Full SweetAlert2 configuration
   * @returns {Promise} SweetAlert2 result
   */
  fire: (config) => {
    return fire(config);
  },
};

// Export for ES6 modules (React components)
export default swalService;

// Also make available globally for Blade templates
if (typeof window !== "undefined") {
  window.SwalService = swalService;
}